import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Sprout, User, LogOut, LayoutDashboard, Search, FileText, Users, Target, Menu, Shield, Palette, Handshake, BookOpen, Building2, BarChart3, Phone, Calendar, ChevronDown, Settings, GraduationCap, MessageSquare, HelpCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const Navbar = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) { setIsAdmin(false); return; }
      try {
        const res = await axios.get(`${API}/api/admin/check`, { headers: { Authorization: `Bearer ${token}` } });
        setIsAdmin(res.data.is_admin);
      } catch { setIsAdmin(false); }
    };
    checkAdmin();
  }, [token]);

  const handleLogout = () => { logout(); navigate('/'); };

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 navbar-glass bg-white/80 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="w-9 h-9 bg-gradient-to-br from-primary to-emerald-600 rounded-xl flex items-center justify-center group-hover:from-primary-800 group-hover:to-emerald-700 transition-all shadow-sm">
              <Sprout className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif font-semibold text-xl text-primary-900">RaiseRoot</span>
          </Link>

          {/* ===== LOGGED-IN NAVIGATION (simplified: Grants + Partners + Learn + Book) ===== */}
          {user ? (
            <div className="hidden md:flex items-center gap-1">
              <Link to="/grants" className="text-stone-600 hover:text-primary font-medium transition-colors flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-stone-50 text-sm" data-testid="nav-grants">
                <Search className="w-4 h-4" />
                Find Grants
              </Link>

              <Link to="/partners" className="text-stone-600 hover:text-primary font-medium transition-colors flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-stone-50 text-sm" data-testid="nav-partners">
                <Handshake className="w-4 h-4" />
                Corporate Partners
              </Link>

              <Link to="/community" className="text-stone-600 hover:text-primary font-medium transition-colors flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-stone-50 text-sm" data-testid="nav-learn">
                <GraduationCap className="w-4 h-4" />
                Learn
              </Link>

              <Link to="/consulting" className="text-primary hover:text-primary-800 font-medium transition-colors flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-primary-50 text-sm" data-testid="nav-consulting">
                <Phone className="w-4 h-4" />
                Book a Session
              </Link>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-6">
              <Link to="/pricing" className="text-stone-600 hover:text-primary font-medium transition-colors" data-testid="nav-pricing">Pricing</Link>
              <Link to="/blog" className="text-stone-600 hover:text-primary font-medium transition-colors" data-testid="nav-blog">Blog</Link>
              <Link to="/consulting" className="text-stone-600 hover:text-primary font-medium transition-colors" data-testid="nav-consulting-guest">Strategy Sessions</Link>
              <Link to="/community" className="text-stone-600 hover:text-primary font-medium transition-colors" data-testid="nav-learn-guest">Learning Hub</Link>
            </div>
          )}

          {/* Right side: User menu / Auth */}
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 rounded-lg hover:bg-stone-100 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? (
                <svg className="w-6 h-6 text-stone-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <Menu className="w-6 h-6 text-stone-700" />
              )}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-primary-50" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:inline text-stone-700 font-medium text-sm">{user.organization_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/organization" className="flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4" /> My Organization
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=overview" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="w-4 h-4" /> Account & Security
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-amber-600" data-testid="nav-admin">
                          <Shield className="w-4 h-4" /> Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="text-stone-600 hover:text-primary hover:bg-primary-50" data-testid="nav-login">Log in</Button></Link>
                <Link to="/register"><Button className="bg-primary hover:bg-primary-800 text-white btn-lift" data-testid="nav-register">Start 7-Day Trial</Button></Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ===== MOBILE MENU ===== */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-200 bg-white/95 backdrop-blur-md max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {user ? (
              <>
                <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2.5 rounded-lg font-medium text-primary bg-primary-50"><LayoutDashboard className="w-4 h-4" /> Dashboard</Link>

                <p className="px-3 pt-3 pb-1 text-[10px] text-stone-400 uppercase tracking-wider font-bold">Core Tools</p>
                <Link to="/grants" className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50"><Search className="w-4 h-4" /> Find Grants</Link>
                <Link to="/partners" className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50"><Handshake className="w-4 h-4" /> Corporate Partners</Link>

                <p className="px-3 pt-3 pb-1 text-[10px] text-stone-400 uppercase tracking-wider font-bold">Learn & Connect</p>
                <Link to="/community" className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50"><GraduationCap className="w-4 h-4" /> Learning Hub</Link>
                <Link to="/consulting" className="flex items-center gap-2 px-3 py-2 rounded-lg text-primary hover:bg-primary-50"><Phone className="w-4 h-4" /> Book a Session</Link>

                <div className="border-t border-stone-100 my-2" />
                <Link to="/organization" className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-600 hover:bg-stone-50"><Building2 className="w-4 h-4" /> My Organization</Link>
                <Link to="/pricing" className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-600 hover:bg-stone-50"><Target className="w-4 h-4" /> Pricing</Link>
                <Link to="/help" className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-600 hover:bg-stone-50"><HelpCircle className="w-4 h-4" /> Help & Support</Link>
                {isAdmin && <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-lg text-amber-600 hover:bg-amber-50"><Shield className="w-4 h-4" /> Admin</Link>}
              </>
            ) : (
              <>
                <Link to="/pricing" className="block px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50 font-medium">Pricing</Link>
                <Link to="/consulting" className="block px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50 font-medium">Strategy Sessions</Link>
                <Link to="/community" className="block px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50 font-medium">Learning Hub</Link>
                <div className="border-t border-stone-100 my-2" />
                <Link to="/login" className="block px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-50 font-medium">Log in</Link>
                <Link to="/register" className="block px-3 py-2 rounded-lg bg-primary text-white text-center font-medium">Start 7-Day Trial</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
